import type { TimeInput } from './types';
import { toDate } from './isomorphic/internal/coerce';
import { getDateTimeFormat } from './isomorphic/internal/formatter-cache';

/** Wall-clock parts of an instant as observed in a specific zone. */
export interface WallTime {
    year: number;
    month: number; // 1-12
    day: number; // 1-31
    hour: number; // 0-23
    minute: number; // 0-59
    second: number; // 0-59
    /** `YYYY-MM-DDTHH:mm` — minute-precision, the `<input type="datetime-local">` shape. */
    localDateTime: string;
}

const pad = (n: number, len = 2): string => String(n).padStart(len, '0');

/**
 * Convert a fixed instant into the wall-clock parts an observer in `zone` would
 * read. The inverse of {@link wallTimeToInstant}; used for authoring round-trips
 * (instant → datetime-local field) and as the convergence probe for the
 * wall-time→instant solver. Lifted from admin `toLocalDatetime`.
 *
 * Returns null for an invalid instant or an invalid zone (rather than silently
 * falling back to the runtime's default zone — that would be non-deterministic
 * across runtimes). Zone values are validated on write (PR2).
 *
 * @sideEffect none — pure.
 */
export function instantToWallTime({
    instant,
    zone,
}: {
    instant: TimeInput;
    zone: string;
}): WallTime | null {
    const d = toDate(instant);
    if (!d) return null;

    try {
        const parts = getDateTimeFormat('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: zone,
        }).formatToParts(d);

        const get = (type: string): string =>
            parts.find((p) => p.type === type)?.value ?? '00';

        const year = Number(get('year'));
        const month = Number(get('month'));
        const day = Number(get('day'));
        // Intl can emit "24" for midnight in some engines — normalize to 0.
        const hour = get('hour') === '24' ? 0 : Number(get('hour'));
        const minute = Number(get('minute'));
        const second = Number(get('second'));

        return {
            year,
            month,
            day,
            hour,
            minute,
            second,
            localDateTime: `${pad(year, 4)}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`,
        };
    } catch {
        return null;
    }
}
